import {GameModel} from "@nolimitcity/slot-game/bin/model/GameModel";
import {
    FlightModeGameData,
    FlightModeInitData,
} from "./FlightModeData";
import {ApiPlugin} from "@nolimitcity/slot-launcher/bin/plugins/ApiPlugin";
import {APIFeatureBetEventData} from "@nolimitcity/slot-launcher/bin/interfaces/APIBetEventData";
import {APIEvent} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {FlightMode} from "../FlightMode";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {SlotKeypad} from "@nolimitcity/slot-keypad/bin/SlotKeypad";

export class FlightModeModel extends GameModel<FlightModeInitData, FlightModeGameData> {

    public isRestore:boolean = false;
    public isRestoreFeatureBet:boolean;
    public buyFeatureName:string = "";
    public wasFeatureBuy:boolean;
    public totalWin:number;
    public winRatio:number;
    public totalWinRatio:number;
    public responseWinRatio:number;
    public isGodMode:boolean;
    public brokeTheBank:boolean;
    public accumulatedRoundWin:number;
    public mode:string;
    public nextMode:string;
    public multiplier:number;
    public freespinsLeft:number;
    public avalancheWin:number;
    public responseWin:number;
    public playedBetValue:number;
    public freespinsPlayed:number;
    public reels:string[][];
    public symbolSizes:number[][];
    public featureBuyTimesBetValue:any;
    public symbolValues:any;
    public extraSpinCost:number;
    public nextPlayerInteraction:any;
    skipAnticipation:boolean;
    isLuckyDraw:boolean;

    constructor(api:ApiPlugin, triggerEvents:boolean = true) {
        super(api, triggerEvents);

        api.events.once("featureBet", (data:boolean) => {
            this.isRestoreFeatureBet = data;
        });
        api.events.once("featureName", (data:string) => {
            this.buyFeatureName = data;
        });

        const handleBet = (betData:APIFeatureBetEventData) => {
            for (let index = 0; index < this.initData.featureBuyTimesBetValue.length; index++) {
                if (this.initData.featureBuyTimesBetValue[index].name === betData.featureName) {
                    this.buyFeatureName = betData.featureName;
                    Logger.logDev("Opted Feature : ", this.buyFeatureName);
                    break;
                } else if (this.data.nextMode == GameMode.NORMAL) {
                    this.buyFeatureName = "";
                }
            }
        };
        FlightMode.apiPlugin.events.on(APIEvent.BET, handleBet);
        FlightMode.apiPlugin.events.on(APIEvent.ACTION_SPINS_BET, handleBet);

        FlightMode.apiPlugin.events.on(APIEvent.SELECTED_FEATURE_BET_CHANGED, () => {
            const activeBetFeature = SlotKeypad.apiPlugIn.betFeatureController.getActiveBetFeature();
        })
    }

    protected onInit(data:FlightModeInitData):void {
        super.onInit(data);
        this.isRestore = data.isRestoreState;
        this.isGodMode = this.buyFeatureName == "GOD_MODE" && this.data.wasFeatureBuy;
        this.accumulatedRoundWin = data.accumulatedRoundWin;
        this.mode = data.mode;
        this.nextMode = data.nextMode;
        this.multiplier = data.multiplier;
        this.freespinsLeft = data.freespinsLeft;
        this.wasFeatureBuy = data.wasFeatureBuy;
        this.avalancheWin = data.spinWin;
        this.symbolSizes = data.symbolSizes;
        this.reels = data.reels;
        this.featureBuyTimesBetValue = data.featureBuyTimesBetValue;
        this.symbolValues = data.symbolValues;
        this.extraSpinCost = data.extraSpinCost;
        this.nextPlayerInteraction = data.nextPlayerInteraction;
        this.skipAnticipation = this.buyFeatureName.startsWith("FREESPIN");
        this.isLuckyDraw = this.buyFeatureName == "LUCKY_DRAW";
    }

    public onGame(data:FlightModeGameData):void {
        super.onGame(data);
        this.isRestore = false;
        this.totalWin = this.gameData.accumulatedRoundWin;
        this.winRatio = this.gameData.spinWin / this.gameData.playedBetValue;
        this.responseWinRatio = this.gameData.win / this.gameData.playedBetValue;
        this.totalWinRatio = this.data.accumulatedRoundWin / this.gameData.playedBetValue;
        this.isGodMode = this.buyFeatureName == "GOD_MODE" && this.data.wasFeatureBuy;
        this.brokeTheBank = this.gameData.brokeTheBank;
        this.accumulatedRoundWin = data.accumulatedRoundWin;
        this.mode = data.mode;
        this.nextMode = data.nextMode;
        this.multiplier = data.multiplier;
        this.freespinsLeft = data.freespinsLeft;
        this.wasFeatureBuy = data.wasFeatureBuy;
        this.avalancheWin = data.spinWin;
        this.responseWin = data.win;
        this.playedBetValue = data.playedBetValue;
        this.reels = data.reels;
        this.symbolSizes = data.sizes;
        this.freespinsPlayed = data.freespinsPlayed;
        this.extraSpinCost = data.extraSpinCost;
        this.nextPlayerInteraction = data.nextPlayerInteraction;
        this.skipAnticipation = this.buyFeatureName.startsWith("FREESPIN");
        this.isLuckyDraw = this.buyFeatureName == "LUCKY_DRAW";
    }

    public isBoostedBet():boolean {
        return SlotKeypad.apiPlugIn.betFeatureController.getActiveBetFeature()?.type == "BOOSTED_BET";
    }

    public getWins() {
        return this.gameData.betWayWins.map((betWay) => {
            return {
                winLineData: betWay,
                winningPositions: this.gameData.evaluatedReels.slice(0, betWay.length)
                    .map((reel, reelIndex) => reel
                        .map((symbol, rowIndex) => {
                            return {
                                won: (betWay.symbol == symbol || symbol == "W") &&
                                      betWay.length > reelIndex,
                                row: rowIndex,
                            }})
                        .filter((pos) => pos.won)
                        .map((pos) => {
                            return {reel: reelIndex, row: pos.row};
                        }))
                    .reduce((a,b) => a.concat(b), [])
            }
        });
    }

    public getWinPositions() {
        return this.gameData.evaluatedReels
            .map((reel, reelIndex) => reel
                .map((symbol, rowIndex) => {
                    return {
                        won: this.gameData.betWayWins.some((betWay) => (betWay.symbol == symbol || symbol == "W") &&
                                                                       betWay.length > reelIndex),
                        row: rowIndex,
                    }})
                .filter((pos) => pos.won)
                .map((pos) => pos.row));
    }

    public anyWinningSymbols() {
        return this.gameData.betWayWins.length > 0;
    }

    public isFirstSpin() {
        return this.mode == GameMode.NORMAL;
    }

    public isBaseGame() {
        return this.mode == GameMode.NORMAL || this.mode == "NORMAL_AVALANCHE"
    }

    public isFreespins() {
        return (this.mode == GameMode.FREESPIN || this.mode == "FREESPIN_AVALANCHE") &&
               !this.data.isExtraSpin;
    }

    public freespinsIsFinished() {
        return this.isFreespins() && (this.nextMode == "NORMAL" || this.nextMode == "EXTRA_SPIN");
    }

    public freespinsShouldStart() {
        return this.isBaseGame() && this.nextMode == "FREESPIN"
    }

    public isNotAvalanche() {
        return this.mode == "NORMAL" || this.mode == "FREESPIN";
    }

    public avalanchesAreDone() {
        return this.mode != "EXTRA_SPIN" && (this.nextMode == GameMode.NORMAL ||
                                             this.nextMode == GameMode.FREESPIN ||
                                             this.nextMode == "EXTRA_SPIN")
    }

    public usedAFreespin() {
        return this.nextMode == GameMode.FREESPIN && this.mode != "EXTRA_SPIN";
    }

    public isExtraSpinPrompt() {
        return this.nextMode == "EXTRA_SPIN";
    }

    public playedExtraSpin() {
        return this.mode == "EXTRA_SPIN" && this.nextMode == "FREESPIN";
    }

    hasStartingMultipliers():boolean {
        return this.buyFeatureName?.startsWith("BOOSTED_BET_STATE");
    }

    startingMultiplier():number {
        return parseInt(this.buyFeatureName.slice(18), 10);
    }

    nothingHappened() {
        return this.data.mode == GameMode.NORMAL
            && this.nextMode == GameMode.NORMAL
            && this.totalWin == 0;
    }

    public getExplodingPositions() {
        return this.gameData.evaluatedReels
            .map((reel, reelIndex) => reel
                .map((symbol, rowIndex) => {
                    return {
                        exploded: this.gameData.betWayWins.some((betWay) => (betWay.symbol == symbol ||
                                                                             symbol == "W") &&
                                                                             betWay.length > reelIndex) ||
                                                                             symbol == "MI" ||
                                                                             symbol == "GM",
                        row: rowIndex,
                    }})
                .filter((pos) => pos.exploded)
                .map((pos) => pos.row));
    }

    public bombPositions() {
        const bombs = [];
        for (let reel = 0; reel < 6; reel++)
            for (let row = 0; row < 3; row++)
                if (this.gameData.reels[reel][row] == "B")
                    bombs.push({reel: reel, row: row});

        return bombs
            .map((bomb) => {
                const positions = [[bomb]]
                for (let depth = 0; depth < 5; depth++) {
                    const layer = []
                    if (bomb.reel + 1 + depth < 6)
                        layer.push({reel: bomb.reel + 1 + depth, row: bomb.row});
                    if (bomb.reel - 1 - depth > -1)
                        layer.push({reel: bomb.reel - 1 - depth, row: bomb.row});
                    if (bomb.row + 1 + depth < 4)
                        layer.push({reel: bomb.reel, row: bomb.row + 1 + depth});
                    if (bomb.row - 1 - depth > -1)
                        layer.push({reel: bomb.reel, row: bomb.row - 1 - depth});
                    positions.push(layer);
                }
                return positions;
            })
            .reduce((a,b) => [
                a[0].concat(b[0]),
                a[1].concat(b[1]),
                a[2].concat(b[2]),
                a[3].concat(b[3]),
                a[4].concat(b[4]),
                a[5].concat(b[5]),
            ], [[],[],[],[],[],[]])
            .map((layer) => {
                return Array.from(new Set(layer));
            });
    }

    public multiplierSymbolPositions() {
        const positions = [];
        for (let reel = 0; reel < 6; reel++)
            for (let row = 0; row < 3; row++)
                if (this.gameData.reels[reel][row] == "MI")
                    positions.push({reel: reel, row: row, multiplier: this.gameData.sizes[reel][row]});
        return positions;
    }

    public xHoleSymbolPosition() {
        const positions = [];
        for (let reel = 0; reel < 6; reel++)
            for (let row = 0; row < 3; row++)
                if (this.data.reels[reel][row] == "XH")
                    positions.push({reel: reel, row: row});
        return positions[0];
    }

    public goldenMultiplierPositions() {
        const positions = [];
        for (let reel = 0; reel < 6; reel++)
            for (let row = 0; row < 3; row++)
                if (this.data.reels[reel][row] == "GM")
                    positions.push({reel: reel, row: row});
        return positions;
    }

    public allMultiplierPositions() {
        const positions = [];
        for (let reel = 0; reel < 6; reel++)
            for (let row = 0; row < 3; row++)
                if (this.data.reels[reel][row] == "GM" || this.data.reels[reel][row] == "MI")
                    positions.push({reel: reel, row: row});
        return positions;
    }

    public postXHolePositions() {
        const startPositions = [];
        for (let reel = 0; reel < 6; reel++)
            for (let row = 0; row < 3; row++)
                startPositions.push({reel: reel, row: row, symbol: this.data.reels[reel][row]});

        const endPositions = [];
        for (let reel = 0; reel < 6; reel++) {
            for (let row = 0; row < 3; row++) {
                if (this.data.reels[reel][row] == "XH")
                    continue;
                const targetSymbol = this.data.reelsAfterAvalanche[reel][row];
                const index = startPositions.findIndex((symbol) => symbol.symbol == targetSymbol);
                const startPosition = startPositions[index];
                startPositions.splice(index, 1);
                endPositions.push({start: startPosition, end: {reel: reel, row: row}});
            }
        }

        return endPositions;
    }

    public scatterCount() {
        return this.data.reels.reduce((a,b) => a.concat(b), []).filter((sym) => sym == "S").length;
    }

    public scatterPositions() {
        const positions = [];
        for (let reel = 0; reel < 6; reel++)
            for (let row = 0; row < 3; row++)
                if (this.data.reels[reel][row] == "S")
                    positions.push({reel: reel, row: row});
        return positions;
    }

    // max win och 5000 with 250x win as threshold for highest level
    // meaning we wont ever be able to reach it if the bet is base bet x20 or higher in cost
    specialWinThresholds():boolean {
        return this.buyFeatureName != "" &&
               this.featureBuyTimesBetValue
                    .find((feature:{name:string}) => feature.name == this.buyFeatureName)!.price > 20;

    }
}